// GSTIN format + Luhn-36 checksum validation.
// Mirrors the backend validator (backend/src/lib/gstin.js) — keep the two in sync.
//
// GSTIN structure (15 chars):
//   [0-1]   State code (01-37)
//   [2-11]  PAN of the entity
//   [12]    Entity code (1-9, A-Z) — registration number for the PAN in that state
//   [13]    Default "Z"
//   [14]    Luhn-36 checksum

// State code: 01-37 plus 97 (Other Territory) and 99 (Centre Jurisdiction).
// The old pattern ([0-3][0-9]|3[0-7]) let 38/39 through — the first branch matched
// them, making the second unreachable. Fixed alongside backend/src/lib/gstin.js.
const GSTIN_REGEX = /^(0[1-9]|[12][0-9]|3[0-7]|97|99)[A-Z]{5}[0-9]{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;

const ALPHABET = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ'; // base-36

function luhn36Checksum(first14: string): string | null {
  // GSTN's algorithm: weight alternates 1, 2, 1, 2, ... starting from leftmost char.
  // For each char: multiply by weight, then sum digits of product in base 36.
  let sum = 0;
  const factor = [1, 2];
  for (let i = 0; i < first14.length; i++) {
    const code = ALPHABET.indexOf(first14[i]);
    if (code < 0) return null;
    const v = code * factor[i % 2];
    // sum of base-36 digits of v
    sum += Math.floor(v / 36) + (v % 36);
  }
  const checkDigit = (36 - (sum % 36)) % 36;
  return ALPHABET[checkDigit];
}

/**
 * Returns true if the GSTIN passes both format + checksum (15th char is the check digit).
 */
export function isValidGstin(gstin: string): boolean {
  if (typeof gstin !== 'string') return false;
  const g = gstin.trim().toUpperCase();
  if (!GSTIN_REGEX.test(g)) return false;
  const expected = luhn36Checksum(g.slice(0, 14));
  return expected !== null && expected === g[14];
}
